import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const TEAM_COLORS = {
  'Red Bull':        '#3671C6', 'Red Bull Racing':  '#3671C6',
  'Ferrari':         '#E8002D',
  'Mercedes':        '#27F4D2',
  'McLaren':         '#FF8000',
  'Aston Martin':    '#229971',
  'Alpine':          '#FF87BC', 'Alpine F1 Team':   '#FF87BC',
  'Williams':        '#64C4FF',
  'AlphaTauri':      '#5E8FAA', 'RB F1 Team':       '#5E8FAA', 'RB':               '#5E8FAA',
  'Alfa Romeo':      '#C92D4B', 'Alfa Romeo Racing':'#C92D4B', 'Kick Sauber':      '#C92D4B',
  'Haas':            '#B6BABD', 'Haas F1 Team':     '#B6BABD',
};

const teamColor = (team) => TEAM_COLORS[team] || '#6b7280';

const FLAGS = {
  'Bahrain': '🇧🇭', 'Saudi Arabia': '🇸🇦', 'Australia': '🇦🇺', 'China': '🇨🇳',
  'Japan': '🇯🇵', 'USA': '🇺🇸', 'United States': '🇺🇸', 'Italy': '🇮🇹',
  'Monaco': '🇲🇨', 'Canada': '🇨🇦', 'Spain': '🇪🇸', 'Austria': '🇦🇹',
  'UK': '🇬🇧', 'United Kingdom': '🇬🇧', 'Hungary': '🇭🇺', 'Belgium': '🇧🇪',
  'Netherlands': '🇳🇱', 'Singapore': '🇸🇬', 'Azerbaijan': '🇦🇿',
  'Mexico': '🇲🇽', 'Brazil': '🇧🇷', 'Las Vegas': '🇺🇸', 'Qatar': '🇶🇦',
  'Abu Dhabi': '🇦🇪', 'UAE': '🇦🇪', 'Miami': '🇺🇸',
};
const flag = (country) => FLAGS[country] || '🏁';

const DriverCard = ({ driver, onClick, placed, selected }) => {
  const color = teamColor(driver.team);
  return (
    <button
      onClick={onClick}
      disabled={placed}
      className={`w-full text-left px-3 py-2 rounded-lg border transition-all duration-150 flex items-center gap-2
        ${placed
          ? 'opacity-20 cursor-not-allowed border-zinc-800 bg-transparent'
          : selected
          ? 'border-f1red bg-f1red/10 scale-[1.02] shadow-lg'
          : 'border-zinc-700 bg-zinc-900 hover:border-zinc-500 hover:bg-zinc-800 cursor-pointer'
        }`}
    >
      <div className="w-1.5 h-8 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div className="min-w-0">
        <p className="text-white font-black text-xs leading-tight truncate">{driver.code}</p>
        <p className="text-zinc-500 text-[9px] truncate">{driver.team}</p>
      </div>
    </button>
  );
};

const GrilleDeDepart = () => {
  const { user, refreshUser } = useAuth();
  const [phase, setPhase] = useState('loading'); // loading | playing | done | already | error
  const [challenge, setChallenge] = useState(null);
  const [grid, setGrid] = useState(Array(20).fill(null)); // 20 slots
  const [drivers, setDrivers] = useState([]);
  const [selected, setSelected] = useState(null); // driverId of selected card
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const hasInitialized = useRef(false);

  const load = useCallback(async () => {
    if (!user) { setPhase('error'); return; }
    try {
      const r = await api.get('/game/grille/today');
      if (r.data.alreadyPlayed) {
        setChallenge(r.data);
        setPhase('already');
      } else {
        setChallenge(r.data);
        setDrivers(r.data.drivers);
        setGrid(Array(r.data.totalPositions || 20).fill(null));
        setPhase('playing');
      }
    } catch (err) {
      setPhase('error');
    }
  }, [user]);

  useEffect(() => {
    if (user && !hasInitialized.current) {
      hasInitialized.current = true;
      load();
    }
  }, [user, load]);

  const placedIds = new Set(grid.filter(Boolean).map(d => d.driverId));

  const handleDriverClick = (driver) => {
    if (placedIds.has(driver.driverId)) return;
    if (selected?.driverId === driver.driverId) {
      setSelected(null);
    } else {
      setSelected(driver);
    }
  };

  const handleSlotClick = (idx) => {
    if (grid[idx]) {
      // Remove from slot → back to pool
      setGrid(prev => { const g = [...prev]; g[idx] = null; return g; });
      setSelected(null);
    } else if (selected) {
      setGrid(prev => { const g = [...prev]; g[idx] = selected; return g; });
      setSelected(null);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    const answers = grid.map(d => d?.driverId || '');
    try {
      const r = await api.post('/game/grille/submit', {
        year: challenge.year,
        round: challenge.round,
        answers,
      });
      setResult(r.data);
      setPhase('done');
      if (r.data.xpEarned > 0) refreshUser();
    } catch (err) {
      setPhase('error');
    } finally {
      setSubmitting(false);
    }
  }, [grid, challenge, submitting, refreshUser]);

  // Auto-submit when all slots filled
  useEffect(() => {
    if (phase !== 'playing') return;
    if (grid.every(Boolean) && !submitting) handleSubmit();
  }, [grid, phase]);

  const filledCount = grid.filter(Boolean).length;

  // ── LOADING ──────────────────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-zinc-400">Chargement du défi du jour...</p>
    </div>
  );


  if (!user) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-zinc-400">Connecte-toi pour jouer.</p>
      <Link to="/login" className="btn-primary">Se connecter</Link>
    </div>
  );

  if (phase === 'error') return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-zinc-400 text-lg">Données indisponibles pour le GP d'aujourd'hui.</p>
      <p className="text-zinc-600 text-sm">Réessaie demain ou vérifie ta connexion.</p>
      <Link to="/box-box" className="btn-secondary">← Box Box Hub</Link>
    </div>
  );

  // ── ALREADY PLAYED ───────────────────────────────────────────────────────
  if (phase === 'already') return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <p className="text-5xl mb-4">✅</p>
      <h1 className="text-2xl font-black text-white mb-2">Déjà joué aujourd'hui !</h1>
      <p className="text-zinc-400 mb-1">Score : <span className="text-white font-bold">{challenge.score}/20</span></p>
      <p className="text-zinc-400 mb-6">XP gagné : <span className="text-f1red font-bold">+{challenge.xpEarned} XP</span></p>
      <p className="text-zinc-500 text-sm mb-6">Reviens demain pour un nouveau GP.</p>
      <Link to="/box-box" className="btn-secondary">← Box Box Hub</Link>
    </div>
  );

  // ── RESULTS ──────────────────────────────────────────────────────────────
  if (phase === 'done' && result) {
    const pct = Math.round((result.correct / result.total) * 100);
    return (
      <div className="max-w-lg mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <p className="text-6xl mb-3">
            {pct === 100 ? '🏆' : pct >= 75 ? '🔥' : pct >= 50 ? '👍' : pct >= 25 ? '😬' : '💀'}
          </p>
          <h1 className="text-3xl font-black text-white">{result.correct}/{result.total}</h1>
          <p className="text-zinc-400 mt-1">{challenge.raceName} {challenge.year}</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-f1red/10 border border-f1red/30">
            <span className="text-f1red font-black text-xl">+{result.xpEarned} XP</span>
          </div>
          {result.levelInfo?.leveledUp && (
            <p className="text-yellow-400 font-black mt-2">🎉 Niveau {result.levelInfo.level} atteint !</p>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-800 overflow-hidden mb-6">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-white font-black text-sm uppercase tracking-wider">Détail</span>
            <span className="text-zinc-500 text-xs">{result.correct} bonnes réponses sur {result.total}</span>
          </div>
          <div className="divide-y divide-zinc-800/60 max-h-96 overflow-y-auto">
            {result.breakdown.map((row, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-2.5 ${row.correct ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
                <span className={`w-5 text-center text-xs font-black ${row.correct ? 'text-green-400' : 'text-red-400'}`}>
                  {row.correct ? '✓' : '✗'}
                </span>
                <span className="text-zinc-500 text-xs w-6 text-right font-mono">P{row.position}</span>
                <span className="text-white text-xs font-bold flex-1">{row.actual}</span>
                {!row.correct && (
                  <span className="text-zinc-600 text-xs">{row.guessed !== '—' ? `Tu as mis: ${row.guessed}` : 'Non rempli'}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Link to="/box-box" className="btn-secondary flex-1 text-center">← Box Box Hub</Link>
        </div>
      </div>
    );
  }

  // ── PLAYING ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-5">
        <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: '#06b6d4' }}>Grille de départ</p>
        <h1 className="text-2xl font-black text-white leading-tight">
          {flag(challenge.country)} {challenge.raceName}
        </h1>
        <p className="text-zinc-500 text-xs">{challenge.year} · {challenge.circuit}</p>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-zinc-800 rounded-full mb-5 overflow-hidden">
        <div className="h-full bg-f1red rounded-full transition-all duration-300"
          style={{ width: `${(filledCount / grid.length) * 100}%` }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — Grid slots */}
        <div>
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-2">
            Grille de départ ({filledCount}/{grid.length}) — clique un slot pour retirer
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {grid.map((driver, idx) => (
              <button key={idx} onClick={() => handleSlotClick(idx)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all
                  ${driver
                    ? 'border-zinc-600 bg-zinc-800 hover:border-red-500/50 hover:bg-red-500/5'
                    : selected
                    ? 'border-f1red/50 bg-f1red/5 animate-pulse cursor-pointer'
                    : 'border-zinc-800 bg-zinc-900/40 cursor-default'
                  }`}
              >
                <span className={`text-xs font-black w-7 shrink-0 ${driver ? 'text-zinc-400' : 'text-zinc-700'}`}>
                  P{idx + 1}
                </span>
                {driver ? (
                  <>
                    <div className="w-1 h-6 rounded-full shrink-0" style={{ backgroundColor: teamColor(driver.team) }} />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-black truncate">{driver.code}</p>
                      <p className="text-zinc-600 text-[9px] truncate">{driver.team}</p>
                    </div>
                  </>
                ) : (
                  <span className="text-zinc-700 text-xs">{selected ? '← placer ici' : 'vide'}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — Driver pool */}
        <div>
          <p className="text-zinc-500 text-xs font-bold uppercase tracking-wider mb-2">
            Pilotes — clique pour sélectionner
          </p>
          <div className="grid grid-cols-2 gap-1.5 max-h-[520px] overflow-y-auto pr-1">
            {drivers.map(driver => (
              <DriverCard
                key={driver.driverId}
                driver={driver}
                placed={placedIds.has(driver.driverId)}
                selected={selected?.driverId === driver.driverId}
                onClick={() => handleDriverClick(driver)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Submit button */}
      <div className="mt-5 flex items-center justify-between gap-4">
        <Link to="/boxbox" className="btn-secondary text-sm py-2 px-4">Abandonner</Link>
        <button
          onClick={() => handleSubmit()}
          disabled={submitting || filledCount === 0}
          className="btn-primary flex-1 max-w-xs py-3 font-black disabled:opacity-40">
          {submitting ? 'Envoi...' : `Valider (${filledCount}/${grid.length})`}
        </button>
      </div>
      <p className="text-zinc-600 text-xs text-center mt-2">
        +100 XP par bonne position · Grille de départ · 1 partie par jour
      </p>
    </div>
  );
};

export default GrilleDeDepart;
